/* =========================================================
   RABESSA AYUBI — interaction layer
   Runs in two modes:
     mode "shopify" — real Cart AJAX API on the storefront
     mode "preview" — local cart state, checkout via Shopify cart permalink
   Currency is NEVER hardcoded: it always comes from the cart/market payload.
   ========================================================= */
(function () {
  "use strict";

  var CFG = window.RABESSA || {};
  var MODE = CFG.mode === "preview" ? "preview" : "shopify";
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------- utilities ---------------- */
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  function money(cents, currency) {
    var cur = currency || CFG.currency || "USD";
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(CFG.locale || document.documentElement.lang || "en", {
        style: "currency",
        currency: cur,
        // zero-decimal currencies format themselves correctly via Intl
        minimumFractionDigits: undefined
      }).format(amount);
    } catch (e) {
      return cur + " " + amount.toFixed(2);
    }
  }

  function announce(msg) {
    var live = $("#a11y-live");
    if (live) { live.textContent = ""; window.setTimeout(function () { live.textContent = msg; }, 40); }
  }

  /* ---------------- analytics (no duplicate tracking) ----------------
     We only push to an existing dataLayer / Shopify analytics. We never
     inject a second pixel or GA tag. */
  function track(event, payload) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push(Object.assign({ event: "rabessa_" + event }, payload || {}));
      }
      document.dispatchEvent(new CustomEvent("rabessa:" + event, { detail: payload || {} }));
    } catch (e) { /* analytics must never break shopping */ }
  }

  /* ---------------- toast ---------------- */
  var toastEl, toastTimer;
  function toast(message, tone) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "toast";
      toastEl.setAttribute("role", "status");
      document.body.appendChild(toastEl);
    }
    toastEl.setAttribute("data-tone", tone || "ok");
    toastEl.textContent = message;
    // force reflow so the transition replays
    void toastEl.offsetWidth;
    toastEl.classList.add("is-visible");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { toastEl.classList.remove("is-visible"); }, 3200);
    announce(message);
  }

  /* ---------------- reveal on scroll ---------------- */
  function initReveal() {
    var els = $$("[data-reveal]");
    if (!els.length) return;
    if (reduceMotion || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var delay = parseInt(entry.target.getAttribute("data-reveal-delay") || "0", 10);
        window.setTimeout(function () { entry.target.classList.add("is-in"); }, Math.min(delay, 400));
        io.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------- mobile nav ---------------- */
  function initNav() {
    var toggle = $("[data-nav-toggle]");
    var nav = $("[data-nav]");
    if (!toggle || !nav) return;
    toggle.addEventListener("click", function () {
      var open = nav.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
    nav.addEventListener("click", function (e) {
      if (e.target.tagName === "A") {
        nav.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
      }
    });
  }

  /* ---------------- accordions (height-animated, no layout shift) ---------------- */
  function initAccordions() {
    $$("[data-acc-btn]").forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute("aria-controls"));
      if (!panel) return;
      var open = btn.getAttribute("aria-expanded") === "true";
      panel.style.height = open ? "auto" : "0px";

      btn.addEventListener("click", function () {
        var isOpen = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", isOpen ? "false" : "true");
        var inner = panel.firstElementChild;
        var target = inner ? inner.getBoundingClientRect().height : 0;

        if (reduceMotion) { panel.style.height = isOpen ? "0px" : "auto"; return; }

        if (isOpen) {
          panel.style.height = target + "px";
          void panel.offsetWidth;
          panel.style.height = "0px";
        } else {
          panel.style.height = "0px";
          void panel.offsetWidth;
          panel.style.height = target + "px";
          panel.addEventListener("transitionend", function once(ev) {
            if (ev.propertyName !== "height") return;
            panel.style.height = "auto";
            panel.removeEventListener("transitionend", once);
          });
        }
      });
    });
  }

  /* =========================================================
     CART ADAPTERS
     ========================================================= */

  var PreviewCart = {
    key: "rabessa_preview_cart",
    state: { items: [], currency: CFG.currency || "USD" },
    load: function () {
      try {
        var raw = window.localStorage.getItem(this.key);
        if (raw) {
          var parsed = JSON.parse(raw);
          if (parsed && Array.isArray(parsed.items)) this.state.items = parsed.items;
        }
      } catch (e) { /* private mode / blocked storage — start empty */ }
      this.state.currency = CFG.currency || "USD";
      return Promise.resolve(this.snapshot());
    },
    persist: function () {
      try { window.localStorage.setItem(this.key, JSON.stringify({ items: this.state.items })); }
      catch (e) { /* nothing to do — cart still works for this page view */ }
    },
    variant: function (id) {
      var found = null;
      (CFG.products || []).forEach(function (p) {
        (p.variants || []).forEach(function (v) { if (String(v.id) === String(id)) found = { product: p, variant: v }; });
      });
      return found;
    },
    snapshot: function () {
      var cur = CFG.currency || "USD";
      var self = this;
      var items = this.state.items.map(function (row) {
        var rec = self.variant(row.id);
        if (!rec) return null;
        var unit = rec.variant.prices[cur];
        if (unit == null) return null;
        return {
          id: rec.variant.id,
          key: String(rec.variant.id),
          product_title: rec.product.title,
          variant_title: rec.variant.title,
          handle: rec.product.handle,
          url: rec.product.url,
          image: rec.variant.image,
          quantity: row.quantity,
          price: Math.round(unit * 100),
          line_price: Math.round(unit * 100) * row.quantity,
          available: rec.variant.available
        };
      }).filter(Boolean);
      var total = items.reduce(function (a, i) { return a + i.line_price; }, 0);
      var count = items.reduce(function (a, i) { return a + i.quantity; }, 0);
      return { items: items, total_price: total, item_count: count, currency: cur };
    },
    add: function (id, qty) {
      var rec = this.variant(id);
      if (!rec) return Promise.reject(new Error("That colour isn’t available right now."));
      if (!rec.variant.available) return Promise.reject(new Error(rec.product.title + " in " + rec.variant.title + " is sold out."));
      var row = null;
      this.state.items.forEach(function (r) { if (String(r.id) === String(id)) row = r; });
      if (row) row.quantity += qty; else this.state.items.push({ id: String(id), quantity: qty });
      this.persist();
      return Promise.resolve(this.snapshot());
    },
    change: function (key, qty) {
      this.state.items = this.state.items
        .map(function (r) { return String(r.id) === String(key) ? { id: r.id, quantity: qty } : r; })
        .filter(function (r) { return r.quantity > 0; });
      this.persist();
      return Promise.resolve(this.snapshot());
    },
    checkoutUrl: function () {
      // Real Shopify cart permalink — Shopify resolves the buyer's market,
      // currency, taxes and shipping at checkout.
      var parts = this.state.items.map(function (r) { return r.id + ":" + r.quantity; });
      if (!parts.length) return null;
      return CFG.storeUrl + "/cart/" + parts.join(",");
    }
  };

  var ShopifyCart = {
    snapshotFrom: function (cart) {
      return {
        items: (cart.items || []).map(function (i) {
          return {
            id: i.variant_id,
            key: i.key,
            product_title: i.product_title,
            variant_title: i.variant_title,
            handle: i.handle,
            url: i.url,
            image: i.image,
            quantity: i.quantity,
            price: i.final_price,
            line_price: i.final_line_price,
            available: true
          };
        }),
        total_price: cart.total_price,
        item_count: cart.item_count,
        currency: cart.currency
      };
    },
    load: function () {
      var self = this;
      return fetch(CFG.routes.cart + ".js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (c) { return self.snapshotFrom(c); });
    },
    add: function (id, qty) {
      var self = this;
      return fetch(CFG.routes.cartAdd, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) {
            throw new Error(res.body && res.body.description ? res.body.description : "We couldn’t add that to your bag.");
          }
          return self.load();
        });
    },
    change: function (key, qty) {
      var self = this;
      return fetch(CFG.routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(function (c) { return self.snapshotFrom(c); });
    },
    checkoutUrl: function () { return (CFG.routes && CFG.routes.checkout) || "/checkout"; }
  };

  var Cart = MODE === "preview" ? PreviewCart : ShopifyCart;

  /* =========================================================
     CART DRAWER
     ========================================================= */
  var Drawer = {
    lastFocus: null,
    latest: null,

    el: function () { return $("[data-drawer]"); },

    open: function () {
      var d = this.el(), scrim = $("[data-drawer-scrim]");
      if (!d) return;
      this.lastFocus = document.activeElement;
      d.classList.add("is-open");
      d.setAttribute("aria-hidden", "false");
      if (scrim) scrim.classList.add("is-open");
      document.body.style.overflow = "hidden";
      var close = $("[data-drawer-close]", d);
      if (close) close.focus();
      document.addEventListener("keydown", this.onKey);
    },

    close: function () {
      var d = this.el(), scrim = $("[data-drawer-scrim]");
      if (!d) return;
      d.classList.remove("is-open");
      d.setAttribute("aria-hidden", "true");
      if (scrim) scrim.classList.remove("is-open");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", Drawer.onKey);
      if (Drawer.lastFocus && Drawer.lastFocus.focus) Drawer.lastFocus.focus();
    },

    onKey: function (e) {
      if (e.key === "Escape") { Drawer.close(); return; }
      if (e.key !== "Tab") return;
      var d = Drawer.el();
      if (!d) return;
      var focusables = $$("a[href],button:not([disabled]),input,select,[tabindex]:not([tabindex='-1'])", d)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },

    render: function (cart) {
      this.latest = cart;
      var body = $("[data-drawer-body]"), foot = $("[data-drawer-foot]");

      // header count badges everywhere
      $$("[data-cart-count]").forEach(function (n) {
        n.textContent = cart.item_count;
        n.setAttribute("data-empty", cart.item_count === 0 ? "true" : "false");
      });

      if (!body) return;

      if (!cart.items.length) {
        body.innerHTML =
          '<div class="cart-empty">' +
          '<p class="h3">Your bag is empty</p>' +
          '<p class="lede" style="font-size:.94rem">Two pieces, both in stock. Free tracked shipping on every order.</p>' +
          '<a class="btn btn--primary" href="' + (CFG.routes.collection || "/collections/all") + '">Shop the collection</a>' +
          "</div>";
        if (foot) foot.hidden = true;
        return;
      }

      if (foot) foot.hidden = false;

      var html = '<ul class="lineitems">';
      cart.items.forEach(function (item) {
        html +=
          '<li class="li" data-line-key="' + item.key + '">' +
          '<img class="li__img" src="' + (item.image || "") + '" alt="" loading="lazy" width="78" height="104">' +
          '<div class="li__main">' +
          '<div class="li__row">' +
          '<div>' +
          '<div class="li__name">' + item.product_title + "</div>" +
          '<div class="li__opt">' + (item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "") + "</div>" +
          "</div>" +
          '<div class="li__price num">' + money(item.line_price, cart.currency) + "</div>" +
          "</div>" +
          '<div class="li__ctrls">' +
          '<div class="qty">' +
          '<button type="button" data-line-dec aria-label="Decrease quantity of ' + item.product_title + '">−</button>' +
          '<input type="text" inputmode="numeric" value="' + item.quantity + '" data-line-qty aria-label="Quantity of ' + item.product_title + '">' +
          '<button type="button" data-line-inc aria-label="Increase quantity of ' + item.product_title + '">+</button>' +
          "</div>" +
          '<button type="button" class="li__remove" data-line-remove>Remove</button>' +
          "</div>" +
          "</div>" +
          "</li>";
      });
      html += "</ul>";
      html += Upsell.markup(cart);
      body.innerHTML = html;

      var totalEl = $("[data-cart-total]");
      if (totalEl) totalEl.textContent = money(cart.total_price, cart.currency);

      Upsell.bind();
    }
  };

  /* =========================================================
     UPSELL — only the other live product, only when eligible
     ========================================================= */
  var Upsell = {
    markup: function (cart) {
      var products = CFG.products || [];
      if (products.length < 2) return "";

      var inCart = {};
      cart.items.forEach(function (i) { inCart[String(i.handle)] = true; });

      // the candidate is a live product that is NOT already in the cart
      var candidate = null;
      products.forEach(function (p) {
        if (candidate) return;
        if (inCart[p.handle]) return;
        var hasStock = (p.variants || []).some(function (v) { return v.available; });
        if (!hasStock) return;              // never recommend an unavailable product
        candidate = p;
      });
      if (!candidate) return "";

      var cur = cart.currency;
      var first = (candidate.variants || []).filter(function (v) { return v.available; })[0];
      if (!first) return "";

      var priceCents = MODE === "preview"
        ? Math.round((first.prices[cur] != null ? first.prices[cur] : 0) * 100)
        : first.price_cents;
      if (!priceCents) return "";

      var sw = (candidate.variants || []).map(function (v, i) {
        return '<li><button type="button" class="swatch" role="radio" ' +
          'aria-checked="' + (v.id === first.id ? "true" : "false") + '" ' +
          'data-available="' + (v.available ? "true" : "false") + '" ' +
          'data-upsell-variant="' + v.id + '" data-price="' + (MODE === "preview" ? Math.round((v.prices[cur] || 0) * 100) : v.price_cents) + '" ' +
          'data-image="' + (v.image || "") + '" ' +
          'aria-label="' + v.title + (v.available ? "" : " — sold out") + '">' +
          (v.image ? '<img src="' + v.image + '" alt="" loading="lazy">' : '<span class="swatch__solid" style="background:' + (v.swatch || "#ccc") + '"></span>') +
          "</button></li>";
      }).join("");

      return (
        '<div class="upsell" data-upsell="' + candidate.handle + '">' +
        '<div class="upsell__head"><span class="upsell__tag">Pairs with</span>' +
        '<strong style="font-family:var(--f-display);font-size:1.15rem">' + candidate.title + "</strong></div>" +
        '<div class="upsell__row">' +
        '<img data-upsell-image src="' + (first.image || "") + '" alt="' + candidate.title + '" loading="lazy" width="64" height="85">' +
        "<div>" +
        '<p class="upsell__why">' + candidate.pair_reason + "</p>" +
        '<div class="price" style="margin-top:8px"><span class="price__now num" data-upsell-price>' + money(priceCents, cur) + "</span></div>" +
        "</div>" +
        "</div>" +
        '<ul class="swatches" role="radiogroup" aria-label="Choose a colour for ' + candidate.title + '">' + sw + "</ul>" +
        '<p class="upsell__err" data-upsell-error hidden></p>' +
        '<button type="button" class="btn btn--sm btn--block" data-upsell-add data-variant="' + first.id + '">Add ' + candidate.title + "</button>" +
        "</div>"
      );
    },

    bind: function () {
      var root = $("[data-upsell]");
      if (!root) return;
      var addBtn = $("[data-upsell-add]", root);
      var img = $("[data-upsell-image]", root);
      var priceEl = $("[data-upsell-price]", root);
      var errEl = $("[data-upsell-error]", root);
      var cur = (Drawer.latest && Drawer.latest.currency) || CFG.currency;

      $$("[data-upsell-variant]", root).forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (btn.getAttribute("data-available") === "false") {
            errEl.hidden = false;
            errEl.textContent = "That colour is sold out. Pick another.";
            return;
          }
          errEl.hidden = true;
          $$("[data-upsell-variant]", root).forEach(function (b) { b.setAttribute("aria-checked", "false"); });
          btn.setAttribute("aria-checked", "true");
          addBtn.setAttribute("data-variant", btn.getAttribute("data-upsell-variant"));
          if (img && btn.getAttribute("data-image")) img.src = btn.getAttribute("data-image");
          if (priceEl) priceEl.textContent = money(parseInt(btn.getAttribute("data-price"), 10), cur);
        });
      });

      addBtn.addEventListener("click", function () {
        addBtn.disabled = true;
        addBtn.textContent = "Adding…";
        addToCart(addBtn.getAttribute("data-variant"), 1, { source: "cart_upsell", keepOpen: true })
          .catch(function () { /* handled in addToCart */ })
          .then(function () { addBtn.disabled = false; });
      });
    }
  };

  /* =========================================================
     ADD TO CART
     ========================================================= */
  function addToCart(variantId, qty, opts) {
    opts = opts || {};
    return Cart.add(variantId, qty)
      .then(function (cart) {
        Drawer.render(cart);
        if (!opts.keepOpen) Drawer.open(); else Drawer.open();
        track("add_to_cart", { variant_id: variantId, quantity: qty, source: opts.source || "pdp", value: cart.total_price / 100, currency: cart.currency });
        if (opts.source === "cart_upsell") track("upsell_accepted", { variant_id: variantId });
        toast("Added to your bag");
        return cart;
      })
      .catch(function (err) {
        toast(err && err.message ? err.message : "We couldn’t add that to your bag.", "error");
        throw err;
      });
  }

  /* =========================================================
     PRODUCT FORM — swatches, gallery sync, availability, sticky bar
     ========================================================= */
  function initProductForm(root) {
    var data = null;
    try { data = JSON.parse($("[data-product-json]", root).textContent); } catch (e) { return; }

    var swatches = $$("[data-variant-btn]", root);
    var mainImg = $("[data-gallery-main]", root);
    var thumbsWrap = $("[data-gallery-thumbs]", root);
    var priceNow = $("[data-price-now]", root);
    var priceWas = $("[data-price-was]", root);
    var priceOff = $("[data-price-off]", root);
    var stockEl = $("[data-stock]", root);
    var addBtn = $("[data-add]", root);
    var optValue = $("[data-opt-value]", root);
    var qtyInput = $("[data-qty-input]", root);
    var current = data.variants.filter(function (v) { return v.available; })[0] || data.variants[0];

    // Price resolution never hardcodes a currency. On the storefront Shopify has
    // already rendered the market's cents into price_cents. In preview mode we
    // look the active currency up in the real presentment-price map.
    function priceCents(v) {
      if (v.prices && CFG.currency && v.prices[CFG.currency] != null) {
        return Math.round(v.prices[CFG.currency] * 100);
      }
      return v.price_cents || 0;
    }
    function compareCents(v) {
      if (v.compare && CFG.currency && v.compare[CFG.currency] != null) {
        return Math.round(v.compare[CFG.currency] * 100);
      }
      return v.compare_cents || 0;
    }

    function imagesFor(variant) {
      var list = data.media.filter(function (m) { return m.color === variant.title; });
      return list.length ? list : data.media;
    }

    function renderThumbs(variant) {
      if (!thumbsWrap) return;
      var imgs = imagesFor(variant);
      thumbsWrap.innerHTML = imgs.map(function (m, i) {
        return '<button type="button" class="gallery__thumb" aria-current="' + (i === 0 ? "true" : "false") +
          '" data-thumb="' + m.src + '" aria-label="View image ' + (i + 1) + ' of ' + imgs.length + '">' +
          '<img src="' + m.src + '" alt="" loading="lazy" width="70" height="70"></button>';
      }).join("");
      $$("[data-thumb]", thumbsWrap).forEach(function (t) {
        t.addEventListener("click", function () {
          $$("[data-thumb]", thumbsWrap).forEach(function (o) { o.setAttribute("aria-current", "false"); });
          t.setAttribute("aria-current", "true");
          swapMain(t.getAttribute("data-thumb"));
        });
      });
    }

    function swapMain(src) {
      if (!mainImg || mainImg.getAttribute("src") === src) return;
      if (reduceMotion) { mainImg.src = src; return; }
      mainImg.classList.add("is-swapping");
      var pre = new Image();
      pre.onload = pre.onerror = function () {
        mainImg.src = src;
        mainImg.classList.remove("is-swapping");
      };
      pre.src = src;
    }

    function select(variant, userInitiated) {
      current = variant;

      swatches.forEach(function (b) {
        b.setAttribute("aria-checked", String(b.getAttribute("data-variant-btn")) === String(variant.id) ? "true" : "false");
      });
      if (optValue) optValue.textContent = variant.title;

      var imgs = imagesFor(variant);
      if (imgs[0]) swapMain(imgs[0].src);
      renderThumbs(variant);

      var pNow = priceCents(variant), pWas = compareCents(variant);
      if (priceNow) priceNow.textContent = money(pNow, CFG.currency);
      if (priceWas) {
        if (pWas && pWas > pNow) {
          priceWas.hidden = false;
          priceWas.textContent = money(pWas, CFG.currency);
          if (priceOff) {
            priceOff.hidden = false;
            priceOff.textContent = "Save " + Math.round((1 - pNow / pWas) * 100) + "%";
          }
        } else {
          priceWas.hidden = true;
          if (priceOff) priceOff.hidden = true;
        }
      }

      if (stockEl) {
        if (!variant.available) {
          stockEl.setAttribute("data-state", "out");
          stockEl.textContent = "Sold out in " + variant.title;
        } else if (variant.inventory != null && variant.inventory > 0 && variant.inventory <= 10 && variant.policy === "DENY") {
          stockEl.setAttribute("data-state", "low");
          stockEl.textContent = "Low stock — " + variant.inventory + " left in " + variant.title;
        } else {
          stockEl.setAttribute("data-state", "in");
          stockEl.textContent = "In stock — ships in 24–48 hours";
        }
      }

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? "Add to bag" : "Sold out";
        addBtn.setAttribute("data-variant", variant.id);
      }

      // sticky bar mirrors the real selection
      var sName = $("[data-sticky-name]");
      var sSub = $("[data-sticky-sub]");
      var sBtn = $("[data-sticky-add]");
      if (sName) sName.textContent = data.title;
      if (sSub) sSub.textContent = variant.title + " · " + money(pNow, CFG.currency);
      if (sBtn) {
        sBtn.setAttribute("data-variant", variant.id);
        sBtn.disabled = !variant.available;
        sBtn.textContent = variant.available ? "Add" : "Sold out";
      }

      if (userInitiated) track("variant_change", { variant_id: variant.id, color: variant.title });
    }

    swatches.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var v = data.variants.filter(function (x) { return String(x.id) === String(btn.getAttribute("data-variant-btn")); })[0];
        if (v) select(v, true);
      });
      btn.addEventListener("keydown", function (e) {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        e.preventDefault();
        var i = swatches.indexOf(btn);
        var next = swatches[(i + (e.key === "ArrowRight" ? 1 : swatches.length - 1)) % swatches.length];
        next.focus(); next.click();
      });
    });

    // quantity
    var dec = $("[data-qty-dec]", root), inc = $("[data-qty-inc]", root);
    function readQty() { return Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1); }
    if (dec) dec.addEventListener("click", function () { qtyInput.value = Math.max(1, readQty() - 1); });
    if (inc) inc.addEventListener("click", function () { qtyInput.value = readQty() + 1; });
    if (qtyInput) qtyInput.addEventListener("change", function () { qtyInput.value = readQty(); });

    if (addBtn) {
      addBtn.addEventListener("click", function () {
        var label = addBtn.textContent;
        addBtn.disabled = true; addBtn.textContent = "Adding…";
        addToCart(current.id, readQty(), { source: "pdp" })
          .catch(function () {})
          .then(function () { addBtn.disabled = false; addBtn.textContent = label; });
      });
    }

    var stickyAdd = $("[data-sticky-add]");
    if (stickyAdd) {
      stickyAdd.addEventListener("click", function () {
        stickyAdd.disabled = true;
        addToCart(current.id, readQty(), { source: "sticky" })
          .catch(function () {})
          .then(function () { stickyAdd.disabled = false; });
      });
    }

    select(current, false);
    track("view_item", { handle: data.handle, title: data.title });

    // Sticky bar appears only once the real add-to-cart has scrolled past the top.
    // The bottom padding is reserved up front (CSS applies it on mobile only), so
    // showing the bar never shifts the layout and it never covers page content.
    var bar = $("[data-stickybuy]");
    if (bar && addBtn) {
      document.body.classList.add("has-stickybuy");
      var ticking = false;
      var syncSticky = function () {
        ticking = false;
        var r = addBtn.getBoundingClientRect();
        bar.classList.toggle("is-visible", r.bottom < 0);
      };
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(syncSticky);
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      syncSticky();
    }
  }

  /* =========================================================
     WIRING
     ========================================================= */
  function initCartUI() {
    $$("[data-drawer-open]").forEach(function (b) {
      b.addEventListener("click", function (e) {
        e.preventDefault();
        Drawer.open();
        track("view_cart", {});
      });
    });
    $$("[data-drawer-close]").forEach(function (b) { b.addEventListener("click", function () { Drawer.close(); }); });
    var scrim = $("[data-drawer-scrim]");
    if (scrim) scrim.addEventListener("click", function () { Drawer.close(); });

    var body = $("[data-drawer-body]");
    if (body) {
      body.addEventListener("click", function (e) {
        var li = e.target.closest(".li");
        if (!li) return;
        var key = li.getAttribute("data-line-key");
        var input = $("[data-line-qty]", li);
        var q = Math.max(0, parseInt(input && input.value, 10) || 0);

        if (e.target.hasAttribute("data-line-inc")) change(key, q + 1);
        else if (e.target.hasAttribute("data-line-dec")) change(key, Math.max(0, q - 1));
        else if (e.target.hasAttribute("data-line-remove")) {
          li.classList.add("is-leaving");
          window.setTimeout(function () { change(key, 0); }, reduceMotion ? 0 : 180);
        }
      });
      body.addEventListener("change", function (e) {
        if (!e.target.hasAttribute("data-line-qty")) return;
        var li = e.target.closest(".li");
        change(li.getAttribute("data-line-key"), Math.max(0, parseInt(e.target.value, 10) || 0));
      });
    }

    function change(key, qty) {
      Cart.change(key, qty).then(function (cart) {
        Drawer.render(cart);
        track("update_cart", { line: key, quantity: qty, value: cart.total_price / 100, currency: cart.currency });
      }).catch(function () { toast("We couldn’t update your bag. Try again.", "error"); });
    }

    var checkout = $("[data-checkout]");
    if (checkout) {
      checkout.addEventListener("click", function (e) {
        var url = Cart.checkoutUrl();
        if (!url) { e.preventDefault(); toast("Your bag is empty.", "error"); return; }
        track("begin_checkout", {
          value: Drawer.latest ? Drawer.latest.total_price / 100 : 0,
          currency: Drawer.latest ? Drawer.latest.currency : CFG.currency
        });
        if (MODE === "preview") { e.preventDefault(); window.open(url, "_blank", "noopener"); }
      });
    }

    Cart.load().then(function (cart) { Drawer.render(cart); }).catch(function () {});
  }

  function init() {
    initReveal();
    initNav();
    initAccordions();
    initCartUI();
    $$("[data-product-form]").forEach(initProductForm);

    // "Add both" — adds one of each live product, never a discount unless one is configured
    var both = $("[data-add-pair]");
    if (both) {
      both.addEventListener("click", function () {
        var ids = (CFG.products || []).map(function (p) {
          var v = (p.variants || []).filter(function (x) { return x.available; })[0];
          return v ? v.id : null;
        }).filter(Boolean);
        if (ids.length < 2) { toast("Both pieces need to be in stock for this.", "error"); return; }
        both.disabled = true;
        addToCart(ids[0], 1, { source: "pair" })
          .then(function () { return addToCart(ids[1], 1, { source: "pair", keepOpen: true }); })
          .catch(function () {})
          .then(function () { both.disabled = false; track("pair_added", {}); });
      });
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  window.RabessaCart = { open: function () { Drawer.open(); }, add: addToCart, toast: toast };
})();
